import { useState } from "react";
import { ChevronDown, ChevronRight, Fuel } from "lucide-react";

const FuelConsumption = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const fuelTypes = [
    {
      id: "hfo",
      name: "Heavy Fuel Oil (HFO)",
      description: "Primary fuel for main engines and boilers",
      calculation: "Sum across all engine systems: mainEngineConsHfo + auxEngineConsHfo + boilerConsHfo + otherConsHfo",
      fields: ["mainEngineConsHfo", "auxEngineConsHfo", "boilerConsHfo", "otherConsHfo"],
      color: "border-orange-200",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
    },
    {
      id: "lfo",
      name: "Light Fuel Oil (LFO)", 
      description: "Used across all engine types",
      calculation: "Sum across all engine systems: mainEngineConsLfo + auxEngineConsLfo + boilerConsLfo + otherConsLfo",
      fields: ["mainEngineConsLfo", "auxEngineConsLfo", "boilerConsLfo", "otherConsLfo"],
      color: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "mgo",
      name: "Marine Gas Oil (MGO)",
      description: "Clean fuel for auxiliary engines and sensitive operations",
      calculation: "Sum across all engine systems: mainEngineConsMgo + auxEngineConsMgo + boilerConsMgo + otherConsMgo",
      fields: ["mainEngineConsMgo", "auxEngineConsMgo", "boilerConsMgo", "otherConsMgo"],
      color: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "biofuel",
      name: "Biofuel",
      description: "Alternative renewable fuel option",
      calculation: "Sum across all engine systems: mainEngineConsBiofuel + auxEngineConsBiofuel + boilerConsBiofuel + otherConsBiofuel",
      fields: ["mainEngineConsBiofuel", "auxEngineConsBiofuel", "boilerConsBiofuel", "otherConsBiofuel"],
      color: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "ethane",
      name: "Ethane",
      description: "Specialized fuel for specific engine types",
      calculation: "Only from main engine: mainEngineConsEthane",
      fields: ["mainEngineConsEthane"],
      color: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
  ];

  const engineSystems = [
    {
      id: "mainEngine",
      name: "Main Engine",
      description: "Primary propulsion system",
      fuelFields: [
        "mainEngineConsHfo", "mainEngineConsLfo", "mainEngineConsMgo", 
        "mainEngineConsBiofuel", "mainEngineConsEthane"
      ],
      color: "border-red-200",
      bgColor: "bg-red-50",
    },
    {
      id: "auxEngine", 
      name: "Auxiliary Engine",
      description: "Power generation and auxiliary systems",
      fuelFields: [
        "auxEngineConsHfo", "auxEngineConsLfo", "auxEngineConsMgo", 
        "auxEngineConsBiofuel"
      ],
      color: "border-blue-200",
      bgColor: "bg-blue-50",
    },
    {
      id: "boiler",
      name: "Boiler System",
      description: "Steam generation and heating",
      fuelFields: [
        "boilerConsHfo", "boilerConsLfo", "boilerConsMgo", 
        "boilerConsBiofuel"
      ],
      color: "border-green-200", 
      bgColor: "bg-green-50",
    },
    {
      id: "other",
      name: "Other Systems",
      description: "Miscellaneous fuel consumption",
      fuelFields: [
        "otherConsHfo", "otherConsLfo", "otherConsMgo", 
        "otherConsBiofuel"
      ],
      color: "border-gray-300",
      bgColor: "bg-gray-100",
    }
  ];

  const dataSourcesInfo = {
    title: "Data Source: VesselEmissionRawData",
    description: "Fuel consumption data is extracted from VesselEmissionRawData collection with specific field names:",
    collections: [
      {
        name: "VesselEmissionRawData",
        purpose: "Primary source of detailed fuel consumption records",
        keyFields: "vesselId, reportDate, voyageId, engine-specific consumption fields"
      }
    ]
  };

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#faeebe] rounded-lg">
              <Fuel className="w-6 h-6 text-[#b7950b]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Fuel Consumption Analytics
              </h2>
              <p className="text-gray-600 text-sm">
                Aggregation methodology from VesselEmissionRawData with voyage/date filtering
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            {/* Data Sources Information */}
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">{dataSourcesInfo.title}</h3>
              <p className="text-gray-600 text-sm mb-3">{dataSourcesInfo.description}</p>
              <div className="grid grid-cols-1 gap-3">
                {dataSourcesInfo.collections.map((collection, index) => (
                  <div key={index} className="p-3 bg-white rounded border border-amber-100">
                    <h4 className="font-semibold text-gray-700 text-sm mb-2">{collection.name}</h4>
                    <p className="text-gray-600 text-xs mb-2">{collection.purpose}</p>
                    <p className="text-gray-500 text-xs font-mono">{collection.keyFields}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Fuel Types */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 mb-3">Fuel Types & Aggregation</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {fuelTypes.map((fuel) => (
                  <div
                    key={fuel.id}
                    className={`p-4 rounded-lg border ${fuel.color} ${fuel.bgColor}`}
                  >
                    <h5 className="font-semibold text-gray-800 mb-2">{fuel.name}</h5>
                    <p className="text-sm text-gray-600 mb-3">{fuel.description}</p>
                    
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-700 mb-1">Aggregation Formula:</p>
                      <div className="p-2 bg-white rounded border border-gray-200">
                        <p className={`text-xs font-mono ${fuel.textColor}`}>
                          {fuel.calculation}
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1">Database Fields:</p>
                      <div className="flex flex-wrap gap-1">
                        {fuel.fields.map((field) => (
                          <span
                            key={field}
                            className="px-2 py-1 bg-white rounded border border-gray-200 text-xs font-mono text-gray-600"
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Engine Systems */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 mb-3">Engine Systems & Fuel Consumption Fields</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {engineSystems.map((system) => (
                  <div
                    key={system.id}
                    className={`p-4 rounded-lg border ${system.color} ${system.bgColor}`}
                  >
                    <h5 className="font-semibold text-gray-800 mb-2">{system.name}</h5>
                    <p className="text-sm text-gray-600 mb-3">{system.description}</p>
                    
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-2">Fuel Consumption Fields:</p>
                      <div className="flex flex-wrap gap-1">
                        {system.fuelFields.map((field) => (
                          <span
                            key={field}
                            className="px-2 py-1 bg-white rounded border border-gray-200 text-xs font-mono text-gray-600"
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelConsumption;